import { SearchableSelect } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FormOptionsModel } from "../../../../data-model/formOptions";
import { RootState } from "../../../../redux/store/store";
import { getAllRelevantOptions } from "../../../../utils/formOptions";
import {
  DX_CODE_CHANGE_OTHER_OPTION,
  DX_CODE_CHANGE_REASONS_FORM_OPTION_ID,
  DX_CODE_CHANGE_SOURCES_FORM_OPTION_ID,
} from "../constants";

const FIELD_CONTAINER_CLASS_NAME = "flex w-1/2 flex-col gap-2";
const OTHER_INPUT_CONTAINER_CLASS_NAME = "flex flex-col gap-1";
const TEXTAREA_BASE_CLASS_NAME =
  "rounded border p-2 text-tiny font-normal text-primaryGray-1";
const TEXTAREA_DEFAULT_BORDER_CLASS_NAME = "border-primaryGray-14";
const TEXTAREA_ERROR_BORDER_CLASS_NAME = "border-tertiaryRed-form_error";
const ERROR_TEXT_CLASS_NAME = "text-xs text-tertiaryRed-form_error";

const buildFormOptions = (
  formOptions: FormOptionsModel[] | undefined,
  optionId: string,
) => {
  const options = (getAllRelevantOptions(formOptions ?? [], optionId) ?? [])
    .filter((option) => option.label && option.value)
    .map((option) => ({
      label: option.label ?? "",
      value: option.value ?? "",
    }));

  // Keep "Other" available for custom entries, but do not duplicate it if the
  // form_options document already owns that option.
  if (options.some((option) => option.value === DX_CODE_CHANGE_OTHER_OPTION)) {
    return options;
  }

  return [
    ...options,
    {
      label: DX_CODE_CHANGE_OTHER_OPTION,
      value: DX_CODE_CHANGE_OTHER_OPTION,
    },
  ];
};

/** Props for the Dx code change reason and source inputs. */
export interface DxCodeChangeReasonFieldsProps {
  userReason: string;
  userSource: string;
  isReasonMissing: boolean;
  isSourceMissing: boolean;
  /** Emits only the selected dropdown option value. */
  onReasonChange: (value: string) => void;
  /** Emits only the selected dropdown option value. */
  onSourceChange: (value: string) => void;
  /** Emits free text typed after selecting the "Other" reason option. */
  onOtherReasonChange: (value: string) => void;
  /** Emits free text typed after selecting the "Other" source option. */
  onOtherSourceChange: (value: string) => void;
}

/**
 * Side-by-side mandatory inputs capturing why a Dx code was changed and the
 * source referred for the change.
 *
 * @param props - Reason/source values, missing-field flags, and change handlers.
 */
export const DxCodeChangeReasonFields = ({
  userReason,
  userSource,
  isReasonMissing,
  isSourceMissing,
  onReasonChange,
  onSourceChange,
  onOtherReasonChange,
  onOtherSourceChange,
}: DxCodeChangeReasonFieldsProps) => {
  const { data: formOptions } = useSelector(
    (state: RootState) => state.formOptions,
  );
  const [selectedReason, setSelectedReason] = useState(userReason);
  const [selectedSource, setSelectedSource] = useState(userSource);
  const [otherReason, setOtherReason] = useState("");
  const [otherSource, setOtherSource] = useState("");
  const isOtherReasonSelected = selectedReason === DX_CODE_CHANGE_OTHER_OPTION;
  const isOtherSourceSelected = selectedSource === DX_CODE_CHANGE_OTHER_OPTION;
  const isOtherReasonMissing = isOtherReasonSelected && isReasonMissing;
  const isOtherSourceMissing = isOtherSourceSelected && isSourceMissing;
  const reasonOptions = useMemo(
    () => buildFormOptions(formOptions, DX_CODE_CHANGE_REASONS_FORM_OPTION_ID),
    [formOptions],
  );
  const sourceOptions = useMemo(
    () => buildFormOptions(formOptions, DX_CODE_CHANGE_SOURCES_FORM_OPTION_ID),
    [formOptions],
  );

  return (
    <div className="dx_code_changes_reason--fields flex w-full gap-2">
      <div className={FIELD_CONTAINER_CLASS_NAME}>
        <SearchableSelect
          id="user_reason"
          label="Reason for Update"
          placeholder="Select the reason for update"
          searchPlaceholder="Search reasons"
          options={reasonOptions}
          value={selectedReason || undefined}
          onOptionChange={({ value }) => {
            setSelectedReason(value);
            onReasonChange(value);
            if (value !== DX_CODE_CHANGE_OTHER_OPTION) {
              setOtherReason("");
              onOtherReasonChange("");
            }
          }}
          required
          error={
            isReasonMissing && !isOtherReasonSelected
              ? "Reason for update is required"
              : ""
          }
        />
        {isOtherReasonSelected && (
          <div className={OTHER_INPUT_CONTAINER_CLASS_NAME}>
            <textarea
              className={`${TEXTAREA_BASE_CLASS_NAME} ${
                isOtherReasonMissing
                  ? TEXTAREA_ERROR_BORDER_CLASS_NAME
                  : TEXTAREA_DEFAULT_BORDER_CLASS_NAME
              }`}
              placeholder="Enter the reason for update"
              rows={5}
              value={otherReason}
              onChange={(e) => {
                setOtherReason(e.target.value);
                onOtherReasonChange(e.target.value);
              }}
              required
              aria-invalid={isOtherReasonMissing}
            />
            {isOtherReasonMissing && (
              <p className={ERROR_TEXT_CLASS_NAME}>Please provide the reason</p>
            )}
          </div>
        )}
      </div>
      <div className={FIELD_CONTAINER_CLASS_NAME}>
        <SearchableSelect
          id="user_source"
          label="Source Referred"
          placeholder="Select the source referred"
          searchPlaceholder="Search sources"
          options={sourceOptions}
          value={selectedSource || undefined}
          onOptionChange={({ value }) => {
            setSelectedSource(value);
            onSourceChange(value);
            if (value !== DX_CODE_CHANGE_OTHER_OPTION) {
              setOtherSource("");
              onOtherSourceChange("");
            }
          }}
          required
          error={
            isSourceMissing && !isOtherSourceSelected
              ? "Source referred is required"
              : ""
          }
        />
        {isOtherSourceSelected && (
          <div className={OTHER_INPUT_CONTAINER_CLASS_NAME}>
            <textarea
              className={`${TEXTAREA_BASE_CLASS_NAME} ${
                isOtherSourceMissing
                  ? TEXTAREA_ERROR_BORDER_CLASS_NAME
                  : TEXTAREA_DEFAULT_BORDER_CLASS_NAME
              }`}
              placeholder="Enter the source referred"
              rows={5}
              value={otherSource}
              onChange={(e) => {
                setOtherSource(e.target.value);
                onOtherSourceChange(e.target.value);
              }}
              required
              aria-invalid={isOtherSourceMissing}
            />
            {isOtherSourceMissing && (
              <p className={ERROR_TEXT_CLASS_NAME}>Please specify the source</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
